import { useDispatch, useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import { NavLink, useLocation } from 'react-router-dom';
import { NavIcon } from '../Icons3D';
import { useBook, useClients, useLogoutUser, useMyFirm, useProfile } from '../../api/hooks';
import { PROFILES, PROFILE_TITLES, ROUTES, isScreenActive } from '../../config/navigation';
import { initials } from '../../lib/format';
import { normaliseRole } from '../../api/session';
import { setAssignedRole, setSidebarOpen, toggleSidebarMini } from '../../state/uiSlice';
import { logout } from '../../state/authSlice';

/** Sections and the signed-in identity — the design's <aside class="side">. */
export default function Sidebar() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { role, sidebarOpen, sidebarMini } = useSelector((s) => s.ui);
  const profile = PROFILES[role];

  /* The design's pills carry demo counts. Show the real ones, and show nothing
     rather than a stale number when there is nothing to count. */
  const clients = useClients();
  const book = useBook();
  const liveCount = (screen, fallback) => {
    if (screen === 'clients') return clients.withMoney.length || clients.rows.length || null;
    if (screen === 'mandates') return book.totals.portfolioCount || null;
    if (screen === 'client360') return null;
    if (screen === 'proposals') return null;
    return /^\d+$/.test(String(fallback ?? '')) ? null : fallback ?? null;
  };

  /* Who is actually signed in. The design's profile is a demo person — its
     name and initials belong to the mock-up, not to this account — so the
     identity comes from the account and its firm, and the design's copy is the
     fallback only until they arrive. */
  const account = useProfile();
  const firm = useMyFirm();
  const me = account.user;
  const myName = me?.name || me?.email || (account.loading ? '…' : profile.name);
  /* Only an APPROVED membership is a seat. A request still waiting says so:
     reading "MFD · Acme Wealth" while every firm write is refused is worse
     than reading nothing. */
  const m = firm.membership;
  const myRole = firm.approved
    ? [PROFILE_TITLES[normaliseRole(m.firmRole)] || m.firmRole, m.organizationName].filter(Boolean).join(' · ')
    : !firm.ready
      ? ''
      : firm.pending
        ? `Awaiting approval${m?.organizationName ? ` at ${m.organizationName}` : ''}`
        : firm.rejected
          ? 'Request declined'
          : 'No firm yet';

  const [revokeSession] = useLogoutUser();
  const client = useApolloClient();

  /**
   * Sign out. `logoutUser` identifies the session by its bearer token, so the
   * server call goes first, best-effort — a dead backend must not trap anyone
   * in a session. Where to land is the route guard's decision, not this
   * handler's; the cache is cleared last because it holds this advisor's book,
   * keyed by query rather than by user.
   */
  const endSession = async () => {
    await revokeSession().catch(() => {});
    dispatch(logout());
    dispatch(setAssignedRole(null));
    await client.clearStore();
  };

  /* Collapsed, the rail is icons only and hovering it opens the full sidebar
     over the page — see .side.mini in design.css. Nothing to do here. */
  const classes = ['side', sidebarOpen && 'open', sidebarMini && 'mini'].filter(Boolean).join(' ');

  return (
    <aside className={classes}>
      <div className="logo">
        <div className="logo-mark">K</div>
        <div className="logo-txt">
          <b>KeepInvesting</b>
          <span>MFD &amp; PMS Suite</span>
        </div>
        <button
          className="side-collapse"
          title={sidebarMini ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={sidebarMini ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => dispatch(toggleSidebarMini())}
        >
          {sidebarMini ? '»' : '«'}
        </button>
      </div>

      <nav className="nav">
        {profile.nav.map(([section, items]) => (
          <div key={section}>
            <h6>{section}</h6>
            {items.map(([screen, icon, label, pill]) => {
              const badge = liveCount(screen, pill);
              const content = (
                <>
                  <NavIcon name={icon} />
                  <span className="nav-label">{label}</span>
                  {badge != null && <span className="pill">{badge}</span>}
                </>
              );
              // Designed destination with no screen yet — shown, not clickable.
              if (!screen) {
                return (
                  <div
                    key={label}
                    className="nav-disabled"
                    style={{ opacity: 0.45, cursor: 'default' }}
                    title="Designed destination — no screen yet"
                  >
                    {content}
                  </div>
                );
              }
              return (
                <NavLink
                  key={label}
                  to={ROUTES[screen]}
                  className={isScreenActive(screen, pathname) ? 'on' : ''}
                  onClick={() => dispatch(setSidebarOpen(false))}
                >
                  {content}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="side-foot">
        {/* The identity is the way into the account's own page, where the seat
            switcher and the rest of the profile live. */}
        <NavLink
          className="me"
          to={ROUTES.profile}
          onClick={() => dispatch(setSidebarOpen(false))}
          title={`${myName} · your profile`}
        >
          <div className="ava">{me?.name || me?.email ? initials(me.name || me.email) : profile.ava}</div>
          <div className="me-id">
            <b>{myName}</b>
            <span>{myRole}</span>
          </div>
        </NavLink>
        {/* Sits with the identity it ends rather than among the topbar's
            icon buttons, where it would be one unlabelled glyph beside four
            others and a mis-click would cost the session. */}
        <button className="icon-btn sign-out" title="Sign out" onClick={endSession}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
            <path d="M16 17l5-5-5-5" />
            <path d="M21 12H9" />
          </svg>
        </button>
      </div>
    </aside>
  );
}
